import { getThemeClass, getThemeComponentGroup } from "@/src_new/shared/lib/themeComponentClasses";
import clsx from "clsx";
import { Controller, RegisterOptions, type FieldPath, type FieldValues } from "react-hook-form";

import type { BaseInputProps } from "@/src_new/shared/types";
import { WrapperInput } from "./WrapperInput";

type TextInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = BaseInputProps<TFieldValues, TName> & {
  formatValueOnBlur?: (value: string) => string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  maxLength?: number;
  type?: React.HTMLInputTypeAttribute;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  rules?: any;
};
type TextInputRules<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = Omit<
  RegisterOptions<TFieldValues, TName>,
  "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
>;
export const inputClassName =
  "h-10 w-full rounded border px-4 text-[13px] outline-none transition disabled:cursor-not-allowed disabled:opacity-60";

export function TextInput<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  control,
  name,
  label,
  placeholder,
  description,
  disabled,
  formatValueOnBlur,
  inputMode,
  maxLength,
  required,
  className,
  onChange,
  type = "text",
  rules,
}: TextInputProps<TFieldValues, TName>) {
  const inputClasses = getThemeComponentGroup("input");

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        required: required ? `${label} is required` : false,
        ...rules,
      }}
      render={({ field, fieldState }) => {
        const inputValue = field.value ?? "";
        const formattedInputValue = formatValueOnBlur
          ? formatValueOnBlur(String(inputValue))
          : inputValue;

        return (
          <WrapperInput
            label={label}
            error={fieldState.error?.message}
            description={description}
            required={required}
            className={className}
          >
            <input
              {...field}
              value={formattedInputValue}
              type={type}
              inputMode={inputMode}
              maxLength={maxLength}
              placeholder={placeholder}
              disabled={disabled}
              onChange={(e) => {
                const fieldValue = formatValueOnBlur
                  ? e.target.value.replace(/,/g, "")
                  : e.target.value;

                field.onChange(fieldValue);
                onChange?.(e);
              }}
              onBlur={(event) => {
                field.onBlur();
              }}
              className={clsx(
                inputClassName,
                getThemeClass(inputClasses.borderColor, "border-theme-border-strong"),
                getThemeClass(inputClasses.bgColor, "bg-theme-surface"),
                getThemeClass(inputClasses.textColor, "text-theme-text"),
                getThemeClass(
                  inputClasses.placeholderColor,
                  "placeholder:text-theme-text-placeholder"
                ),
                getThemeClass(inputClasses.focusBorderColor, "focus:border-theme-primary"),
                "focus:ring-2",
                getThemeClass(inputClasses.focusRingColor, "focus:ring-theme-primary/10"),
                inputClasses.base,
                fieldState.error &&
                  clsx(
                    inputClasses.errorBorderColor || "border-theme-danger-bright",
                    "focus:ring-theme-danger-bright/10"
                  )
              )}
            />
          </WrapperInput>
        );
      }}
    />
  );
}
